/**
 * @fileoverview Mapeamento de variáveis do template Meta para campos do contato.
 * Detecta `{{N}}` no body do template selecionado e oferece, para cada,
 * um dropdown de campo (name, number, email, customField:X).
 */

import React, { useEffect, useMemo, useState } from "react";
import api from "../../../../services/api";
import { extractTemplateVars } from "../../../../utils/templateVars";

const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: "rgba(255,255,255,0.7)",
  marginBottom: 4,
  display: "block",
};
const selectStyle = {
  width: "100%",
  padding: "7px 10px",
  borderRadius: 6,
  border: "1px solid rgba(255,255,255,0.08)",
  background: "rgba(255,255,255,0.03)",
  color: "rgba(255,255,255,0.9)",
  fontSize: 12,
  outline: "none",
  boxSizing: "border-box",
  cursor: "pointer",
};

const BASE_FIELDS = [
  { value: "", label: "— Não mapeado —" },
  { value: "name", label: "Nome do contato" },
  { value: "number", label: "Número" },
  { value: "email", label: "Email" },
];

const extractVariables = extractTemplateVars;

const TemplateVariableMapper = ({ selectedTemplate, mapping, onChange }) => {
  const [customNames, setCustomNames] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(false);

  const body = selectedTemplate?.bodyText || "";
  const variables = useMemo(() => extractVariables(body), [body]);

  useEffect(() => {
    if (variables.length === 0) return;
    let cancelled = false;
    setLoadingCustom(true);
    api
      .get("/contacts/custom-field-names")
      .then(({ data }) => {
        if (!cancelled) setCustomNames(Array.isArray(data?.names) ? data.names : []);
      })
      .catch(() => {
        if (!cancelled) setCustomNames([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCustom(false);
      });
    return () => {
      cancelled = true;
    };
  }, [variables.length]);

  // Se o template mudou e o mapping tem chaves que não existem mais, limpa.
  useEffect(() => {
    const current = mapping || {};
    const keysInMapping = Object.keys(current);
    const extras = keysInMapping.filter(k => !variables.includes(k));
    if (extras.length > 0) {
      const next = { ...current };
      extras.forEach(k => delete next[k]);
      onChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables.join(",")]);

  if (variables.length === 0) return null;

  const update = (key, value) => {
    const next = { ...(mapping || {}) };
    if (!value) delete next[key];
    else next[key] = value;
    onChange(next);
  };

  // Preview com valores fictícios (usando rótulos dos campos) pra mostrar
  // como o template vai ficar após substituição.
  const previewBody = variables.reduce((acc, k) => {
    const field = (mapping || {})[k];
    let label = "(sem valor)";
    if (field === "name") label = "João Silva";
    else if (field === "number") label = "5511999998888";
    else if (field === "email") label = "joao@email.com";
    else if (field && field.startsWith("customField:")) {
      label = `[${field.slice("customField:".length)}]`;
    }
    return acc.split(`{{${k}}}`).join(label);
  }, body);

  const options = [
    ...BASE_FIELDS,
    ...customNames.map(n => ({
      value: `customField:${n}`,
      label: `Campo personalizado: ${n}`,
    })),
  ];

  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.45)",
          marginBottom: 8,
        }}
      >
        Variáveis do template
      </div>

      {variables.map(k => {
        const current = (mapping || {})[k] || "";
        return (
          <div key={k} style={{ marginBottom: 8 }}>
            <label style={labelStyle}>
              <span
                style={{
                  fontFamily: "monospace",
                  color: "#a5b4fc",
                  marginRight: 6,
                  padding: "1px 5px",
                  borderRadius: 4,
                  background: "rgba(99,102,241,0.15)",
                  fontWeight: 700,
                }}
              >
                {`{{${k}}}`}
              </span>
              →
            </label>
            <select
              style={selectStyle}
              value={current}
              onChange={e => update(k, e.target.value)}
              disabled={loadingCustom}
            >
              {options.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        );
      })}

      <div
        style={{
          marginTop: 10,
          padding: "8px 10px",
          borderRadius: 6,
          background: "rgba(255,255,255,0.03)",
          border: "1px dashed rgba(255,255,255,0.1)",
          fontSize: 11,
          color: "rgba(255,255,255,0.65)",
          whiteSpace: "pre-wrap",
          lineHeight: 1.5,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.4)",
            marginBottom: 4,
          }}
        >
          Prévia
        </div>
        {previewBody}
      </div>
    </div>
  );
};

export default TemplateVariableMapper;
